import { useBoolean } from '@chakra-ui/hooks'
import { Box, BoxProps, Flex } from '@chakra-ui/layout'
import { PopoverContent } from '@chakra-ui/react'
import { ResponsiveValue, SystemStyleObject } from '@chakra-ui/styled-system'
import React, {
  ForwardedRef,
  forwardRef,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Virtuoso } from 'react-virtuoso'
import { ZIndex } from '~/types/ThemeTypes'

import { MenuStyleValues, useMenuStyles } from '../componentStyles/Menu.style'
import { MenuItem, MenuListItem } from './MenuItem'

const END_REACHED_COOLDOWN_MS = 100

export const enum MenuPlacement {
  BottomLeft = 'bottom-left',
  BottomCenter = 'bottom-center',
  BottomRight = 'bottom-right',
  TopLeft = 'top-left',
  TopRight = 'top-right',
  RightTop = 'right-top',
  RightBottom = 'right-bottom',
  LeftTop = 'left-top',
  LeftBottom = 'left-bottom',
}

export type MenuOptions = {
  width?: ResponsiveValue<string>
  maxHeight?: ResponsiveValue<string>
  placement?: MenuPlacement
}

type MenuProps<TValue> = {
  items: MenuItem<TValue>[]
  anchorRef?: React.RefObject<HTMLElement>
  onSelect?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  options?: MenuOptions
  header?: React.ReactNode
  headerWrapperVariant?: 'default' | 'divided'
}

export const Menu = <TValue,>({
  items,
  anchorRef,
  onSelect,
  onScrollEndReached,
  options,
  header,
  headerWrapperVariant,
}: MenuProps<TValue>) => {
  const [renderPass, setRenderPass] = useState<number>(0)
  const [endReached, setEndReached] = useBoolean(false)
  const scrollerRef = React.useRef<HTMLElement | null | Window>()
  const boxScrollerParentRef = React.useRef<HTMLDivElement>(null)

  const handleItemSelected = (
    e: React.MouseEvent,
    value: TValue,
    index: number,
    disabled: boolean,
    itemCallback?: (value: TValue, index: number) => void,
  ) => {
    e.stopPropagation()
    if (disabled) return
    onSelect?.(value, index)
    itemCallback?.(value, index)
  }

  const handleScrollEndReachedInternal = () => {
    if (endReached) {
      return
    }

    //only report scroll end reached if there are items in the menu
    if (!items || items.length <= 0) {
      return
    }

    onScrollEndReached?.()

    // Set cooldown to prevent multiple calls
    setEndReached.on()
    setTimeout(() => {
      setEndReached.off()
    }, END_REACHED_COOLDOWN_MS)
  }

  let style: SystemStyleObject | undefined

  if (anchorRef?.current) {
    const anchorElementHeight = anchorRef.current.clientHeight
    const anchorElementWidth = anchorRef.current.clientWidth

    const yAdjustment = `calc(${MenuStyleValues.anchoredYGap} + ${anchorElementHeight}px)`
    const xAdjustment = `calc(${MenuStyleValues.anchoredXGap} + ${anchorElementWidth}px)`

    style = {
      position: 'absolute',
      zIndex: ZIndex.popover,
      width: options?.width ?? anchorElementWidth,
    }

    const placement = options?.placement ?? MenuPlacement.BottomLeft
    switch (placement) {
      case MenuPlacement.BottomCenter:
        style.top = yAdjustment
        style.left = '50%'
        style.transform = 'translateX(-50%)'
        break
      case MenuPlacement.BottomLeft:
        style.top = yAdjustment
        style.left = 0
        break
      case MenuPlacement.BottomRight:
        style.top = yAdjustment
        style.right = 0
        break
      case MenuPlacement.TopLeft:
        style.bottom = yAdjustment
        style.left = 0
        break
      case MenuPlacement.TopRight:
        style.bottom = yAdjustment
        style.right = 0
        break
      case MenuPlacement.RightTop:
        style.top = 0
        style.left = xAdjustment
        break
      case MenuPlacement.RightBottom:
        style.bottom = 0
        style.left = xAdjustment
        break
      case MenuPlacement.LeftTop:
        style.top = 0
        style.right = xAdjustment
        break
      case MenuPlacement.LeftBottom:
        style.bottom = 0
        style.right = xAdjustment
        break
    }
  } else {
    style = { width: options?.width ?? MenuStyleValues.defaultWidth }
  }

  const listContainerRef = (scrollerRef?.current as HTMLElement)?.firstChild
    ?.firstChild as HTMLElement | undefined

  const listHeight = useRef(0)
  const boxHeight = listContainerRef?.clientHeight
  const computedBoxHeight = listHeight.current
    ? `calc(${listHeight.current}px)`
    : '1px'

  const shouldBeVisible = useMemo(() => {
    return boxHeight || listHeight.current > 0
  }, [boxHeight, renderPass])

  const styles = useMenuStyles()

  return (
    <Box sx={{ ...styles.container, ...style }} ref={boxScrollerParentRef}>
      {header && (
        <Flex
          sx={
            headerWrapperVariant === 'divided'
              ? styles.dividedHeaderContainer
              : styles.headerContainer
          }
        >
          {header}
        </Flex>
      )}

      <Flex
        visibility={shouldBeVisible ? 'visible' : 'hidden'}
        sx={styles.scrollerContainer}
        maxHeight={options?.maxHeight ?? MenuStyleValues.defaultMaxHeight}
        height={computedBoxHeight}
      >
        <Virtuoso
          data={items}
          scrollerRef={(ref) => (scrollerRef.current = ref)}
          style={{
            display: 'flex',
            height: '100%',
          }}
          totalListHeightChanged={(height) => {
            listHeight.current = height
            setRenderPass((prev) => prev + 1)
          }}
          itemContent={(index, data: MenuItem<TValue>) => {
            let displayContent: React.JSX.Element

            if (typeof data.content === 'string') {
              displayContent = <MenuListItem primaryText={data.content} />
            } else {
              displayContent = data.content
            }

            if (displayContent.props?.hide) {
              return null
            }

            return (
              <Flex
                sx={styles.itemContainer}
                pt={index ? MenuStyleValues.menuItemsGap : '0px'}
                key={index}
                onClick={(e) =>
                  handleItemSelected(
                    e,
                    data.value,
                    index,
                    data.clickDisabled ?? false,
                    data.onSelect,
                  )
                }
              >
                {displayContent}
              </Flex>
            )
          }}
          endReached={handleScrollEndReachedInternal}
          overscan={500}
        />
      </Flex>
    </Box>
  )
}

export const MenuPositioningWrapper = forwardRef(
  (props: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      <Box ref={ref} position="relative">
        {props.children}
      </Box>
    )
  },
)

export const MenuPopoverWrapper = forwardRef(
  ({ children, ...rest }: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      <Flex
        ref={ref}
        zIndex={ZIndex.popover}
        position="relative"
        sx={{ ...rest }}
      >
        <PopoverContent width="fit-content" bg="transparent">
          {children}
        </PopoverContent>
      </Flex>
    )
  },
)
